"use client";

import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toCsv, type CsvColumn } from "@/lib/export/csv";
import { useAuth } from "@/providers/auth-provider";
import { useUIStore } from "@/stores/ui-store";

/**
 * Saves the rows the surface is showing, as it is showing them, as a CSV.
 * The anchor click runs inside the click handler on purpose: a download
 * started after an await is one the browser is allowed to block.
 */
export function ExportCsvButton<T>({
	rows,
	columns,
	fileName,
	disabled,
	className,
}: {
	rows: readonly T[] | null | undefined;
	columns: readonly CsvColumn<T>[];
	fileName: string;
	disabled?: boolean;
	className?: string;
}) {
	const addToast = useUIStore((s) => s.addToast);
	const { user } = useAuth();
	const count = rows?.length ?? 0;

	const onClick = () => {
		if (!rows || count === 0) return;
		if (!user) {
			window.dispatchEvent(
				new CustomEvent("open-login-dialog", {
					detail: {
						title: "Sign in to export CSV",
						description: "It's free. Your exports stay one click away.",
					},
				}),
			);
			return;
		}
		try {
			const blob = new Blob([toCsv(rows, columns)], { type: "text/csv;charset=utf-8" });
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = fileName;
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(url);
			addToast(`Exported ${count} row${count === 1 ? "" : "s"} to ${fileName}`, "success", 3000);
		} catch (err) {
			addToast(`Export failed: ${err instanceof Error ? err.message : String(err)}`, "error");
		}
	};

	return (
		<Button
			type="button"
			size="xs"
			variant="outline"
			aria-label="Export CSV"
			title="Export CSV"
			disabled={disabled || count === 0}
			onClick={onClick}
			className={className}
		>
			<Download className="size-3.5" />
			<span className="hidden sm:inline">Export CSV</span>
		</Button>
	);
}
